import React, { useState, useEffect } from "react";
import { Company } from "../types";
import { companiesAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";
import { Plus, Building, IndianRupee, Edit2, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "job" as "job" | "internship",
    startDate: "",
    endDate: "",
    ctc: "",
    base: "",
    medical: "",
    joiningBonus: "",
    relocationBonus: "",
    otherBenefits: "",
    stipend: "",
    notes: "",
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const data = await companiesAPI.getCompanies();
      setCompanies(data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError("Company name is required");
      return;
    }

    if (formData.type === "job") {
      if (!formData.ctc || !formData.base) {
        setError("CTC and Base salary are required for job type");
        return;
      }
      const ctc = parseFloat(formData.ctc);
      const base = parseFloat(formData.base);
      if (isNaN(ctc) || ctc <= 0) {
        setError("Please enter a valid CTC amount");
        return;
      }
      if (isNaN(base) || base <= 0) {
        setError("Please enter a valid base salary amount");
        return;
      }
    } else if (formData.type === "internship") {
      if (!formData.stipend) {
        setError("Stipend is required for internship type");
        return;
      }
      const stipend = parseFloat(formData.stipend);
      if (isNaN(stipend) || stipend <= 0) {
        setError("Please enter a valid stipend amount");
        return;
      }
    }

    try {
      setError("");
      const data: any = {
        name: formData.name.trim(),
        type: formData.type,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        notes: formData.notes.trim(),
      };

      if (formData.type === "job") {
        data.ctc = parseFloat(formData.ctc);
        data.base = parseFloat(formData.base);
        data.medical = formData.medical ? parseFloat(formData.medical) : 0;
        data.joiningBonus = formData.joiningBonus ? parseFloat(formData.joiningBonus) : 0;
        data.relocationBonus = formData.relocationBonus ? parseFloat(formData.relocationBonus) : 0;
        data.otherBenefits = formData.otherBenefits ? parseFloat(formData.otherBenefits) : 0;
      } else {
        data.stipend = parseFloat(formData.stipend);
        data.relocationBonus = formData.relocationBonus ? parseFloat(formData.relocationBonus) : 0;
      }

      if (editingCompany) {
        await companiesAPI.updateCompany(editingCompany._id, data);
      } else {
        await companiesAPI.createCompany(data);
      }

      resetForm();
      fetchCompanies();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save company");
    }
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      type: company.type || "job",
      startDate: company.startDate ? company.startDate.split('T')[0] : "",
      endDate: company.endDate ? company.endDate.split('T')[0] : "",
      ctc: company.ctc?.toString() || "",
      base: company.base?.toString() || "",
      medical: company.medical?.toString() || "",
      joiningBonus: company.joiningBonus?.toString() || "",
      relocationBonus: company.relocationBonus?.toString() || "",
      otherBenefits: company.otherBenefits?.toString() || "",
      stipend: company.stipend?.toString() || "",
      notes: company.notes || "",
    });
    setShowAddForm(true);
  };

  const handleDelete = async (companyId: string) => {
    if (window.confirm("Are you sure you want to delete this company?")) {
      try {
        await companiesAPI.deleteCompany(companyId);
        fetchCompanies();
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to delete company");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      type: "job" as "job" | "internship",
      startDate: "",
      endDate: "",
      ctc: "",
      base: "",
      medical: "",
      joiningBonus: "",
      relocationBonus: "",
      otherBenefits: "",
      stipend: "",
      notes: "",
    });
    setShowAddForm(false);
    setEditingCompany(null);
    setError("");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="p-6 max-w-7xl mx-auto">
          <LoadingSkeleton variant="table" count={3} />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Building className="w-8 h-8 text-red-600" />
              Companies
            </h1>
            <p className="text-gray-600 mt-2">Manage your company data and salary information</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Add Company
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingCompany ? "Edit Company" : "Add New Company"}
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as "job" | "internship" })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="job">Full-time Job</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  
                  {/* Job-specific fields */}
                  {formData.type === "job" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CTC (Annual) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          value={formData.ctc}
                          onChange={(e) => setFormData({ ...formData, ctc: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Base Salary *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          value={formData.base}
                          onChange={(e) => setFormData({ ...formData, base: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Medical Benefits
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.medical}
                          onChange={(e) => setFormData({ ...formData, medical: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Joining Bonus
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.joiningBonus}
                          onChange={(e) => setFormData({ ...formData, joiningBonus: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Other Benefits
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.otherBenefits}
                          onChange={(e) => setFormData({ ...formData, otherBenefits: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0.00"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Internship-specific fields */}
                  {formData.type === "internship" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Stipend *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={formData.stipend}
                        onChange={(e) => setFormData({ ...formData, stipend: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                  
                  {/* Relocation bonus for both job and internship */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Relocation Bonus
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.relocationBonus}
                      onChange={(e) => setFormData({ ...formData, relocationBonus: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Additional notes about the company..."
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-colors"
                  >
                    {editingCompany ? "Update Company" : "Add Company"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Companies Grid */}
        {companies.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No companies yet</h3>
            <p className="text-gray-600 mb-6">Start by adding your first company</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-colors"
            >
              Add Company
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company) => (
              <div
                key={company._id}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-200"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 truncate">
                        {company.name}
                      </h3>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                        company.type === 'internship' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {company.type === 'internship' ? 'Internship' : 'Full-time Job'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(company)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(company._id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Date Range */}
                  {(company.startDate || company.endDate) && (
                    <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-1">Duration</div>
                      <div className="text-sm text-gray-700">
                        {company.startDate && new Date(company.startDate).toLocaleDateString('en-IN')} 
                        {company.startDate && company.endDate && ' - '}
                        {company.endDate && new Date(company.endDate).toLocaleDateString('en-IN')}
                        {company.startDate && !company.endDate && ' - Present'}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {company.type === 'job' ? (
                      // Job fields
                      <>
                        {company.ctc && company.ctc > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">CTC (Annual)</span>
                            <span className="font-semibold text-green-600">{formatCurrency(company.ctc)}</span>
                          </div>
                        )}
                        {company.base && company.base > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Base Salary</span>
                            <span className="font-semibold">{formatCurrency(company.base)}</span>
                          </div>
                        )}
                        {company.medical && company.medical > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Medical</span>
                            <span className="font-semibold">{formatCurrency(company.medical)}</span>
                          </div>
                        )}
                        {company.joiningBonus && company.joiningBonus > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Joining Bonus</span>
                            <span className="font-semibold">{formatCurrency(company.joiningBonus)}</span>
                          </div>
                        )}
                        {company.otherBenefits && company.otherBenefits > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Other Benefits</span>
                            <span className="font-semibold">{formatCurrency(company.otherBenefits)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      // Internship fields
                      company.stipend && company.stipend > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Monthly Stipend</span>
                          <span className="font-semibold text-blue-600">{formatCurrency(company.stipend)}</span>
                        </div>
                      )
                    )}
                    {/* Relocation bonus for both types */}
                    {company.relocationBonus && company.relocationBonus > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Relocation Bonus</span>
                        <span className="font-semibold">{formatCurrency(company.relocationBonus)}</span>
                      </div>
                    )}
                  </div>
                  
                  {company.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{company.notes}</p>
                    </div>
                  )}
                  
                  <Link
                    to={`/companies/${company._id}/salary`}
                    className="mt-4 w-full bg-red-50 text-red-600 py-3 rounded-xl hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                  >
                    <IndianRupee className="w-5 h-5" />
                    {company.type === 'internship' ? 'View Stipend Details' : 'View Salary Details'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default Companies;